import * as React from "react"
import * as RechartsPrimitive from "recharts"
import { cn } from "@/lib/utils"
// Format: { THEME_NAME: CSS_SELECTOR }
const THEMES = { light: "", dark: ".dark" } as const
export type ChartConfig = {
  [k in string]: {
    label?: React.ReactNode
    icon?: React.ComponentType
  } & (
    | { color?: string; theme?: never }
    | { color?: never; theme: Record<keyof typeof THEMES, string> }
  )
}
export type ChartLegendItem = {
  key: string
  label: React.ReactNode
  color?: string
  icon?: React.ComponentType
}

function getChartLegendItems(config: ChartConfig, keys = Object.keys(config)): ChartLegendItem[] {
  return keys.map((key) => ({
    key,
    label: config[key]?.label ?? key,
    color: config[key]?.color,
    icon: config[key]?.icon,
  }))
}
type ChartContextProps = {
  config: ChartConfig
}
const ChartContext = React.createContext<ChartContextProps | null>(null)
function useChart() {
  const context = React.useContext(ChartContext)
  if (!context) {
    throw new Error("useChart must be used within a <ChartContainer />")
  }
  return context
}
function ChartContainer({
  id,
  className,
  children,
  legend,
  config,
  ...props
}: React.ComponentProps<"div"> & {
  config: ChartConfig
  legend?: React.ReactNode
  children: React.ComponentProps<
    typeof RechartsPrimitive.ResponsiveContainer
  >["children"]
}) {
  const uniqueId = React.useId()
  const chartId = `chart-${id || uniqueId.replace(/:/g, "")}`
  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          "[&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border flex aspect-video justify-center text-xs [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
          legend && 'min-h-0 flex-col',
          className
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        {legend ? (
          <>
            <div className="min-h-0 flex-1">
              <RechartsPrimitive.ResponsiveContainer>
                {children}
              </RechartsPrimitive.ResponsiveContainer>
            </div>
            <div className="shrink-0">{legend}</div>
          </>
        ) : (
          <RechartsPrimitive.ResponsiveContainer>
            {children}
          </RechartsPrimitive.ResponsiveContainer>
        )}
      </div>
    </ChartContext.Provider>
  )
}
const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const colorConfig = Object.entries(config).filter(
    ([, config]) => config.theme || config.color
  )
  if (!colorConfig.length) {
    return null
  }
  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    const color =
      itemConfig.theme?.[theme as keyof typeof itemConfig.theme] ||
      itemConfig.color
    return color ? `  --color-${key}: ${color};` : null
  })
  .join("\n")}
}
`
          )
          .join("\n"),
      }}
    />
  )
}
const ChartTooltip = RechartsPrimitive.Tooltip
function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = "dot",
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  valueFormatter,
  color,
  nameKey,
  labelKey,
}: React.ComponentProps<typeof RechartsPrimitive.Tooltip> &
  React.ComponentProps<"div"> & {
    hideLabel?: boolean
    hideIndicator?: boolean
    indicator?: "line" | "dot" | "dashed"
    nameKey?: string
    labelKey?: string
    valueFormatter?: (value: unknown) => React.ReactNode
  }) {
  const { config } = useChart()
  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null
    }
    const [item] = payload
    const key = `${labelKey || item?.dataKey || item?.name || "value"}`
    const itemConfig = getPayloadConfigFromPayload(config, item, key)
    const value =
      !labelKey && typeof label === "string"
        ? config[label as keyof typeof config]?.label || label
        : itemConfig?.label
    if (labelFormatter) {
      return (
        <div className={cn("font-medium", labelClassName)}>
          {labelFormatter(value, payload)}
        </div>
      )
    }
    if (!value) {
      return null
    }
    return <div className={cn("font-medium", labelClassName)}>{value}</div>
  }, [
    label,
    labelFormatter,
    payload,
    hideLabel,
    labelClassName,
    config,
    labelKey,
  ])
  if (!active || !payload?.length) {
    return null
  }
  const nestLabel = payload.length === 1 && indicator !== "dot"
  return (
    <div
      className={cn(
        "border-border/50 bg-offwhite grid min-w-[8rem] items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs shadow-xl",
        className
      )}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className="grid gap-1.5">
        {payload.map((item, index) => {
          const key = `${nameKey || item.name || item.dataKey || "value"}`
          const itemConfig = getPayloadConfigFromPayload(config, item, key)
          const indicatorColor = color || item.payload.fill || item.color || itemConfig?.color
          return (
            <div
              key={item.dataKey}
              className={cn(
                "[&>svg]:text-muted-foreground flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5",
                indicator === "dot" && "items-center"
              )}
            >
              {formatter && item?.value !== undefined && item.name ? (
                formatter(item.value, item.name, item, index, item.payload)
              ) : (
                <>
                  {itemConfig?.icon ? (
                    <itemConfig.icon />
                  ) : (
                    !hideIndicator && (
                      <div
                        className={cn(
                          "shrink-0 rounded-[2px]",
                          {
                            "h-2.5 w-2.5": indicator === "dot",
                            "w-1": indicator === "line",
                            "w-0 border-[1.5px] border-dashed":
                              indicator === "dashed",
                            "my-0.5": nestLabel && indicator === "dashed",
                          }
                        )}
                        style={
                          {
                            backgroundColor: indicatorColor,
                            borderColor: indicatorColor,
                          } as React.CSSProperties
                        }
                      />
                    )
                  )}
                  <div
                    className={cn(
                      "flex flex-1 justify-between leading-none",
                      nestLabel ? "items-end" : "items-center"
                    )}
                  >
                    <div className="grid gap-1.5">
                      {nestLabel ? tooltipLabel : null}
                      <span className="text-muted-foreground">
                        {itemConfig?.label || item.name}
                      </span>
                    </div>
                    {item.value && (
                      <span className="text-foreground font-mono font-medium tabular-nums">
                        {valueFormatter ? valueFormatter(item.value) : item.value.toLocaleString()}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
const ChartLegend = RechartsPrimitive.Legend
function ChartLegendContent({
  clickHandler,
  className,
  hideIcon = false,
  payload,
  verticalAlign = "bottom",
  layout = "horizontal",
  align = "center",
  nameKey,
  items,
}: React.ComponentProps<"div"> &
  Pick<RechartsPrimitive.LegendProps, "payload" | "verticalAlign" | "layout" | "align"> & {
    hideIcon?: boolean
    nameKey?: string
    clickHandler?: (dataKey: string) => void
    items?: ChartLegendItem[]
  }) {
  const chartContext = React.useContext(ChartContext)
  const config = chartContext?.config ?? {}
  const legendItems =
    items ??
    payload?.map((item, index) => {
      const key = `${nameKey || item.dataKey || "value"}`
      const itemConfig = getPayloadConfigFromPayload(config, item, key)
      const preferred = item.color || itemConfig?.color
      const indicatorColor =
        typeof preferred === "string" && preferred.trim().startsWith("url(")
          ? itemConfig?.color
          : preferred

      return {
        key: String(item.dataKey ?? item.value ?? index),
        label: itemConfig?.label ?? item.value,
        color: indicatorColor,
        icon: itemConfig?.icon,
      }
    })

  if (!legendItems?.length) {
    return null
  }
  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-1",
        align === "left" && "justify-start",
        align === "right" && "justify-end",
        layout === "vertical" && "flex-col items-start gap-1",
        verticalAlign === "top" ? "pb-3" : "pt-3",
        className
      )}
    >
      {legendItems.map((item) => {
        const itemConfig = config[item.key]
        const IndicatorIcon = item.icon || itemConfig?.icon
        const label = item.label ?? itemConfig?.label ?? item.key
        const indicatorColor = item.color || itemConfig?.color || `var(--color-${item.key})`
        const handleClick = clickHandler ? () => clickHandler(item.key) : undefined

        return (
          <div
            key={item.key}
            className={cn(
              "[&>svg]:text-muted-foreground flex max-w-full shrink-0 items-center gap-1.5 whitespace-nowrap [&>svg]:h-3 [&>svg]:w-3"
            )}
            role={handleClick ? "button" : undefined}
            onClick={handleClick}
          >
            {IndicatorIcon && !hideIcon ? (
              <IndicatorIcon />
            ) : (
              <div
                className="h-2 w-2 shrink-0 rounded-[2px]"
                style={{
                  backgroundColor: indicatorColor as string,
                }}
              />
            )}
            <span className="min-w-0 max-w-full truncate" title={typeof label === "string" ? label : undefined}>
              {label}
            </span>
          </div>
        )
      })}
    </div>
  )
}
// Helper to extract item config from a payload.
function getPayloadConfigFromPayload(
  config: ChartConfig,
  payload: unknown,
  key: string
) {
  if (typeof payload !== "object" || payload === null) {
    return undefined
  }
  const payloadPayload =
    "payload" in payload &&
    typeof payload.payload === "object" &&
    payload.payload !== null
      ? payload.payload
      : undefined
  let configLabelKey: string = key
  if (
    key in payload &&
    typeof payload[key as keyof typeof payload] === "string"
  ) {
    configLabelKey = payload[key as keyof typeof payload] as string
  } else if (
    payloadPayload &&
    key in payloadPayload &&
    typeof payloadPayload[key as keyof typeof payloadPayload] === "string"
  ) {
    configLabelKey = payloadPayload[
      key as keyof typeof payloadPayload
    ] as string
  }
  return configLabelKey in config
    ? config[configLabelKey]
    : config[key as keyof typeof config]
}
export {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  getChartLegendItems,
  ChartStyle,
}